import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingUp, Clock, ArrowUpRight } from "lucide-react";

const WEEKS_PER_YEAR = 52;
const WEEKS_PER_MONTH = 52 / 12; // ~4.333

function formatEuro(n) {
  const safe = Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(safe);
}

function formatHours(n) {
  const safe = Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  return new Intl.NumberFormat("en-US").format(safe);
}

function Slider({ id, label, value, min, max, step, onChange, unit, testid }) {
  const filled = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <label
          htmlFor={id}
          className="text-[11px] uppercase tracking-[0.22em] text-[#9BA1A6] font-semibold"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {label}
        </label>
        <div className="flex items-baseline gap-1">
          <span
            className="text-white text-2xl font-bold tabular-nums"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {value}
          </span>
          {unit && (
            <span
              className="text-[#9BA1A6] text-xs"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {unit}
            </span>
          )}
        </div>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="le-slider w-full"
        style={{ "--filled": `${filled}%` }}
      />
      <div
        className="flex justify-between text-[10px] tracking-widest text-[#5E6470] uppercase"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

export function SavingsCalculator({ t, onCtaClick }) {
  const [hours, setHours] = useState(5);
  const [employees, setEmployees] = useState(3);
  const [cost, setCost] = useState(25);

  const { annual, monthly, hoursSaved } = useMemo(() => {
    const annualHours = hours * employees * WEEKS_PER_YEAR;
    const annualSavings = annualHours * cost;
    const monthlySavings = hours * employees * WEEKS_PER_MONTH * cost;
    return { annual: annualSavings, monthly: monthlySavings, hoursSaved: annualHours };
  }, [hours, employees, cost]);

  return (
    <section id="calculator" className="relative section-pad border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Left: copy */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-[#00E5FF]" />
              <span className="eyebrow">{t.calculator.eyebrow}</span>
            </div>
            <h2
              className="text-white font-semibold leading-[1.05] tracking-tight"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "clamp(30px, 4.5vw, 48px)",
              }}
            >
              {t.calculator.heading}
            </h2>
            <p
              className="mt-6 text-[#9BA1A6] leading-relaxed max-w-md"
              style={{ fontFamily: "'Manrope', sans-serif", fontSize: "16px" }}
            >
              {t.calculator.sub}
            </p>

            <div className="hidden md:flex items-center gap-3 mt-10 text-[#9BA1A6]">
              <Calculator size={16} className="text-[#00E5FF]" />
              <span
                className="text-[11px] uppercase tracking-[0.22em] font-semibold"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                ROI estimator
              </span>
            </div>
          </div>

          {/* Right: calc panel */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="md:col-span-7 bg-[#1C2331] border border-white/10 rounded-sm p-7 md:p-10"
          >
            <div className="space-y-9">
              <Slider
                id="calc-hours"
                label={t.calculator.hours_label}
                value={hours}
                min={1}
                max={40}
                step={1}
                unit="h/wk"
                onChange={setHours}
                testid="calc-hours"
              />
              <Slider
                id="calc-employees"
                label={t.calculator.employees_label}
                value={employees}
                min={1}
                max={50}
                step={1}
                unit="people"
                onChange={setEmployees}
                testid="calc-employees"
              />
              <Slider
                id="calc-cost"
                label={t.calculator.cost_label}
                value={cost}
                min={5}
                max={150}
                step={1}
                unit="€/h"
                onChange={setCost}
                testid="calc-cost"
              />
            </div>

            {/* Results */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
              <ResultCell
                icon={<TrendingUp size={16} className="text-[#00E5FF]" />}
                label={t.calculator.monthly_label}
                value={formatEuro(monthly)}
                testid="calc-monthly"
              />
              <ResultCell
                icon={<TrendingUp size={16} className="text-[#00E5FF]" />}
                label={t.calculator.annual_label}
                value={formatEuro(annual)}
                highlight
                testid="calc-annual"
              />
              <ResultCell
                icon={<Clock size={16} className="text-[#00E5FF]" />}
                label={t.calculator.hours_saved_label}
                value={`${formatHours(hoursSaved)} h`}
                testid="calc-hours-saved"
              />
            </div>

            <p
              className="mt-6 text-[#9BA1A6] text-xs leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {t.calculator.footnote}
            </p>

            <button
              type="button"
              onClick={onCtaClick}
              data-testid="calculator-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00C2D6] text-[#0B132B] font-bold px-7 py-3.5 rounded-sm transition-all duration-300"
              style={{
                fontFamily: "'Manrope', sans-serif",
                letterSpacing: "0.06em",
                boxShadow: "0 0 25px rgba(0,229,255,0.18)",
              }}
            >
              {t.calculator.cta}
              <ArrowUpRight size={16} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ResultCell({ icon, label, value, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative p-6 ${highlight ? "bg-[#0B132B]" : "bg-[#1C2331]"}`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span
          className="text-[10px] uppercase tracking-[0.22em] text-[#9BA1A6] font-semibold"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {label}
        </span>
      </div>
      <div
        className={`mt-4 leading-none font-bold tabular-nums break-words ${
          highlight ? "text-[#00E5FF]" : "text-white"
        }`}
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: highlight ? "clamp(22px, 2.4vw, 30px)" : "clamp(18px, 2vw, 24px)",
        }}
      >
        {value}
      </div>
    </div>
  );
}
